// @flow
import React from "react";
import classNames from "classnames";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import withStyles from "elevate-ui/withStyles";
import Typography from "elevate-ui/Typography";
import Select from "elevate-ui/Select";
import Button from "elevate-ui/Button";
import Input from "elevate-ui/Input";
import { withSellerLeadContext } from "../common/SellerLeadContext";
import { withGlobalContext } from "../common/GlobalContext";

type $Props = {
  classes: Object,
  handlePage3: Function,
  buttonColor: string,
};

class SellerPage3 extends React.Component<$Props> {
  props: $Props;
  componentDidMount() {
    window.scrollTo(0, 0);
  }
  render() {
    const { classes, handlePage3, buttonColor } = this.props;
    const intent = [
      { label: "Sell Immediately", value: "Sell immediately" },
      { label: "Sell Within 3 - 6 Months", value: "Sell within 3-6 months" },
      { label: "Sell Within 6 - 12 Months", value: "Sell within 6-12 months" },
      { label: "Sell In A Year Or More", value: "Sell in a year or more" },
      { label: "Refinance", value: "Refinance" },
      { label: "Stay Where I'm At", value: "Stay where I’m at" },
    ];

    const homeCondition = [
      { label: "Perfect Condition", value: "Perfect Condition" },
      { label: "Good Condition", value: "Good Condition" },
      { label: "Average Condition", value: "Average Condition" },
      { label: "Poor Condition", value: "Poor Condition" },
      { label: "Fixer-Upper", value: "Fixer-Upper" },
    ];

    return (
      <div className={classNames(classes.boxWrapper)}>
        <div className={classes.box}>
          <Typography
            type="title"
            className={classNames(classes.boxText, classes.boxTitle)}
          >
            Final Step… Your Home Valuation Report Is Being Prepared!
          </Typography>
          <Typography
            type="body"
            className={classNames(classes.boxText, classes.boxSubText)}
          >
            This final step helps us to take into account your specific local
            market conditions and time-frame.{" "}
            <span className={classes.boldText}>
              Completing this will provide you with a much more accurate
              valuation over any basic auto generated compter estimate.
            </span>
          </Typography>
          <Typography
            type="body"
            className={classNames(
              classes.boxText,
              classes.boxSubText,
              classes.secondSubText
            )}
          >
            Please finish your request and help us improve the accuracy of your
            valuation by answering the following:
          </Typography>
          <Formik
            initialValues={{
              intent: "",
              homeCondition: "",
              phone: "",
            }}
            validationSchema={() =>
              Yup.object().shape({
                homeCondition: Yup.string().required(
                  "Home Condition is required"
                ),
                intent: Yup.string().required("Intent is required"),
                phone: Yup.string().required("Phone is required"),
              })
            }
            onSubmit={(values, { setSubmitting }) => {
              handlePage3(values);
              setSubmitting(false);
            }}
            render={({ errors, touched }) => (
              <Form noValidate>
                <div className={classes.boxForm}>
                  <div className={classes.boxSelectWrapper}>
                    <Field
                      id="homeCondition"
                      name="homeCondition"
                      component={Select}
                      items={homeCondition}
                      inputProps={{ placeholder: "My Home Is In..." }}
                      className={classNames(
                        classes.boxSelect,
                        errors.homeCondition && touched.homeCondition
                          ? classes.invalid
                          : null
                      )}
                      withScaffold={true}
                    />
                  </div>
                  <div className={classes.boxSelectWrapper}>
                    <Field
                      id="intent"
                      name="intent"
                      component={Select}
                      required
                      inputProps={{ placeholder: "I'm Looking To..." }}
                      items={intent}
                      className={classNames(
                        classes.boxSelect,
                        errors.intent && touched.intent ? classes.invalid : null
                      )}
                      withScaffold={true}
                    />
                  </div>
                  <div className={classes.boxSelectWrapper}>
                    <Field
                      id="phone"
                      name="phone"
                      placeholder="Phone"
                      component={Input}
                      className={classNames(
                        classes.input,
                        errors.phone && touched.phone ? classes.invalid : null
                      )}
                      withScaffold={true}
                    />
                    <div className={classes.helperText}>
                      (We'll text you a link so you can view the report on your
                      phone)
                    </div>
                  </div>
                </div>
                <div className={classes.submitWrapper}>
                  <div className={classes.boxButtonWrapper}>
                    <Button
                      color={buttonColor}
                      className={classes.button}
                      type="submit"
                      innerClassName={classes.buttonInner}
                    >
                      Submit
                    </Button>
                    <div
                      className={classNames(
                        classes.helperText,
                        classes.tcpaText
                      )}
                    >
                      Protecting Your Privacy Is A Top Priority.
                    </div>
                  </div>
                </div>
                <Typography
                  type="body"
                  className={classNames(classes.tcpaText, classes.tcpa)}
                >
                  By clicking “Submit”, I provide my signature expressly
                  consenting that real estate professionals and Elm Street
                  Technology may contact me at the number provided via text,
                  email or call (may involve automated or pre-recorded means). I
                  am not required to enter into this agreement as a condition of
                  any purchase. I can revoke this consent through any reasonable
                  means.
                </Typography>
              </Form>
            )}
          />
        </div>
      </div>
    );
  }
}

const styles = (theme) => {
  const tabletL = 992;
  const tabletP = 768;
  const mobile = 480;

  return {
    boxWrapper: {
      fontFamily: "sans-serif",
      width: "100%",
      [`@media (min-width: ${tabletP}px)`]: {
        width: "80%",
      },
      maxWidth: "1000px",
      display: "flex",
      zIndex: "1",
      position: "relative",
      flexFlow: "column",
      [`@media (min-width: ${mobile}px)`]: {
        marginTop: "calc(3% - 20px)",
      },
      marginBottom: "calc(10%)",
      alignItems: "center",
      justifyContent: "center",
    },
    box: {
      borderRadius: `${theme.spacing.unit * 1.5}px`,
      backgroundColor: theme.colors.white,
      padding: `${theme.spacing.unit * 0.5}px ${theme.spacing.unit}px`,
      [`@media (min-width: ${tabletP}px)`]: {
        padding: `${theme.spacing.unit * 2}px`,
      },
      [`@media (min-width: ${tabletL}px)`]: {
        padding: `${theme.spacing.unit * 3}px`,
      },
      textAlign: "left",
      boxShadow: theme.shadows[4],
    },
    boxTitle: {
      paddingBottom: `${theme.spacing.unit * 0.5}px`,
      fontSize: "20px",
      [`@media (min-width: ${tabletP}px)`]: {
        fontSize: "36px",
      },
    },
    boxText: {
      lineHeight: "1.2",
      fontWeight: "normal",
    },
    tcpa: { textAlign: "left" },
    tcpaText: {
      fontSize: "12px",
      lineHeight: "1.2",
      marginBottom: `${theme.spacing.unit}px`,
    },
    boxSubText: {
      textAlign: "left",
      fontSize: "12px",
      [`@media (min-width: ${tabletP}px)`]: {
        fontSize: "14px",
      },
      paddingBottom: `${theme.spacing.unit}px`,
    },
    secondSubText: {
      display: "inherit",
    },
    boxForm: {
      display: "flex",
      flexFlow: "column wrap",
      [`@media (min-width: ${tabletP}px)`]: {
        flexDirection: "row",
      },
      alignItems: "baseline",
      width: "100%",
    },
    boxSelectWrapper: {
      flex: "1",
      padding: "0 12px",
      width: "100%",
      [`@media (min-width: ${tabletP}px)`]: {
        width: "32%",
      },
      marginBottom: `${theme.spacing.unit * 0.5}px`,
      "&:last-child": {
        [`@media (min-width: ${mobile}px)`]: {
          marginBottom: `${theme.spacing.unit}px`,
        },
      },
    },
    boxSelect: {
      borderRadius: "4px",
      backgroundColor: "#ebffff",
      "& input": { backgroundColor: "#ebffff" },
    },
    input: { borderRadius: "4px", backgroundColor: "#ebffff" },
    submitWrapper: { "text-align": "center" },
    boxButtonWrapper: { display: "inline-block" },
    boldText: { fontWeight: "bold" },
    button: { padding: `2px ${theme.spacing.unit}px` },
    buttonInner: {
      color: theme.colors.white,
      fontSize: "26px",
      textShadow: "1px 1px #521601",
      fontWeight: "700",
      whiteSpace: "nowrap",
    },
    helperText: {
      marginTop: "4px",
      fontSize: "12px",
    },
    inputInvalid: {
      fontSize: "12px",
      marginTop: "4px",
      marginLeft: "14px",
      textAlign: "left",
      color: "darkred",
    },
  };
};

export default withGlobalContext(
  withSellerLeadContext(withStyles(styles)(SellerPage3))
);
